import { Tooltip } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import Delete from 'components/structure/Booking/BookingForms/delete';
import Save from 'components/structure/Booking/BookingForms/save';
import { format, roundToNearestMinutes } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import BookingService, { BookingProps } from 'services/hooks/booking-service';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import ResourceService, { ResourceProps } from 'services/hooks/resource-service';
import { FilterSelect } from 'utils/FilterSelectUtils';
// eslint-disable-next-line
import Select, { SelectChangeEvent } from '@mui/material/Select';
import BlankImageIcon from 'assets/Icons/modals/BlankImageIcon';
import { LiveBookingsTable } from 'components/structure/Booking/BookingForms/BookingTable/LiveBookingsTable';
import { liveIndicatorStyle } from 'utils/styles';
import variables from 'variables';

const LAST_SELECTED_BUILDING_STORAGE_KEY = 'bl.bookings.live.building';

export default function BookingsLive() {
  const [getLiveBookings, setGetLiveBookings] = useState<BookingProps[] | null>([]);
  const [selectedBuildingUuid, setSelectedBuildingUuid] = useState<string>('');
  const SIXTY_SECONDS_INTERVAL = 60000;

  // Fetch all live bookings
  const { isLoading: isLiveBookingsLoading, data: liveBookingsData } = useQuery<
    BookingProps[],
    Error
  >(
    ['liveBookings', selectedBuildingUuid],
    () => BookingService.findLiveBookingsByBuilding(selectedBuildingUuid),
    {
      enabled: !!selectedBuildingUuid,
      refetchInterval: SIXTY_SECONDS_INTERVAL, // Refetch live bookings of the selected building every 60 seconds
      onError: () => {
        toast.error('Error fetching live bookings!');
      }
    }
  );

  // Fetch all buildings
  const { data: buildingsData, isLoading: isBuildingLoading } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: () => {
        toast.error('Error fetching buildings!');
      }
    }
  );

  // Fetch all resources
  const { data: resourcesData, isLoading: isResourceLoading } = useQuery<ResourceProps[], Error>(
    'resources',
    ResourceService.findAllResources,
    {
      onError: () => {
        toast.error('Error fetching resources!');
      }
    }
  );

  // useEffect to sort bookings according to start date
  useEffect(() => {
    if (liveBookingsData && Array.isArray(liveBookingsData)) {
      const sortedBookings = [...liveBookingsData].sort(
        (a, b) => new Date(a.start).valueOf() - new Date(b.start).valueOf()
      );

      setGetLiveBookings(sortedBookings);
    }
  }, [liveBookingsData]);

  // Get the last selected building uuid from local storage
  const lsSelectedBuildingUuid = localStorage.getItem(LAST_SELECTED_BUILDING_STORAGE_KEY);

  // useEffect to restore the last selected building from local storage
  useEffect(() => {
    // If no building is selected (e.g. initial load), use the last selection stored in local storage (if it exists)
    if (
      !!buildingsData &&
      !selectedBuildingUuid &&
      lsSelectedBuildingUuid &&
      buildingsData.find((building) => building.uuid === lsSelectedBuildingUuid)
    ) {
      setSelectedBuildingUuid(lsSelectedBuildingUuid);
    }
  }, [buildingsData, lsSelectedBuildingUuid, selectedBuildingUuid]);

  const handleBuildingChange = (event: SelectChangeEvent) => {
    const buildingUuid = event.target.value as string;
    setSelectedBuildingUuid(buildingUuid);
    // Store the selected building uuid in local storage
    localStorage.setItem(LAST_SELECTED_BUILDING_STORAGE_KEY, buildingUuid);
  };

  const columns = React.useMemo(
    () => [
      {
        id: 'image',
        Header: 'Image',
        accessor: (row: { resource: string }) => {
          if (resourcesData?.find((resource) => resource.uuid === row.resource)?.image) {
            return (
              <img
                src={
                  variables.RESOURCE_IMAGE_URL +
                  resourcesData?.find((resource) => resource.uuid === row.resource)?.image
                }
                className="w-36 m-0 p-0 rounded-l-lg"
              ></img>
            );
          } else {
            return <BlankImageIcon />;
          }
        },
        width: 150,
        key: 'image',
        disableFilters: true
      },
      {
        id: 'resourceName',
        Header: 'Amenity',
        accessor: (row: { resource: string }) => {
          return resourcesData?.find((resource) => resource.uuid === row.resource)?.name;
        },
        Filter: FilterSelect
      },
      {
        id: 'bookedBy',
        Header: 'Booked By',
        accessor: (row: {
          MobileUser: { firstName: any; lastName: any };
          createdAt: string;
          updatedAt: string;
          outlookBooking?: boolean;
          outlookOrganizer?: string;
        }) => {
          return row.outlookBooking
            ? row.outlookOrganizer
            : row.MobileUser.firstName + ' ' + row.MobileUser.lastName;
        },
        disableFilters: true
      },
      {
        id: 'reason',
        Header: 'Reason',
        accessor: (row: { reason: any }) => row.reason,
        disableFilters: true
      },
      {
        id: 'resourceType',
        Header: 'Type',
        accessor: (row: { resource: string }) => {
          return resourcesData?.find((resource) => resource.uuid === row.resource)?.type;
        },
        disableFilters: true
      },
      {
        id: 'time',
        Header: 'Time',
        accessor: (row: { start: string; end: string; buildingTimezone: string }) => {
          const timeZone = row.buildingTimezone;

          // Convert start and end date to Date format (in provided timezone)
          const startDate = utcToZonedTime(new Date(row.start), timeZone);
          const endDate = utcToZonedTime(new Date(row.end), timeZone);

          //Below code is formatting the `startDate` variable to a string representation of the time in the format "h:mm a".
          const formattedStartTime = format(startDate, 'p');
          // Below code is formatting the `endDate` variable to a string representation of the time in the format "h:mm a".
          const formattedEndTime = format(roundToNearestMinutes(endDate, { nearestTo: 15 }), 'p');

          return `${formattedStartTime} - ${formattedEndTime}`;
        },
        disableFilters: true
      }
    ],
    [resourcesData]
  );

  return (
    <div className="booking" style={{ marginTop: 40 }}>
      <h1 style={{ paddingBottom: 20 }}>
        Bookings{' '}
        <span style={liveIndicatorStyle}>
          <Tooltip title={'Refreshes every minute'}>
            <span>● Live Data</span>
          </Tooltip>
        </span>
      </h1>
      <Grid container justifyContent="right" style={{ paddingBottom: 20 }}>
        <Grid item style={{ width: 300, marginRight: 20 }}>
          <FormControl fullWidth size="small" disabled={isBuildingLoading}>
            <InputLabel>Building</InputLabel>
            <Select
              value={selectedBuildingUuid}
              sx={{ borderRadius: '10px', backgroundColor: 'white' }}
              disabled={!buildingsData}
              onChange={handleBuildingChange}
              label={<>Building</>}
            >
              {!!buildingsData &&
                buildingsData
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((building) => {
                    return (
                      <MenuItem key={building.uuid} value={building.uuid}>
                        {building.name}
                      </MenuItem>
                    );
                  })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {!isLiveBookingsLoading && !isResourceLoading && getLiveBookings ? (
        <LiveBookingsTable
          name="Bookings"
          columns={columns}
          data={getLiveBookings}
          CreateForm={Save}
          DeleteForm={Delete}
          createQuery={BookingService.createBooking}
          updateQuery={BookingService.updateBooking}
          deleteQuery={BookingService.deleteBookingById}
          editQuery={BookingService.findBookingById}
          queryName="bookings"
        />
      ) : (
        <div>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
}
